import React from "react";
import { TextField, Typography } from "@mui/material";

import { RenderHTML } from "components";

import { ErrorMessage } from "../../shared/styled-components";
import { useTextBoxQuestionStyles } from "./style";
import { ErrorMessages } from "utils/constants";

interface TextBoxQuestionProps {
  questionId: string;
  questionText: string;
  isMandatory: boolean;
  questionHint?: string | null;
  value: string;
  hasError?: boolean;
  isNumeric?: boolean;
  onChange: (questionId: string, value: string) => void;
}

export const TextBoxQuestion: React.FC<TextBoxQuestionProps> = ({
  questionId,
  questionText,
  isMandatory,
  questionHint,
  value,
  hasError = false,
  isNumeric = false,
  onChange,
}) => {
  const classes = useTextBoxQuestionStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    if (isNumeric) {
      // Keep only digits for numeric fields to prevent scientific notation and symbols.
      inputValue = inputValue.replace(/\D/g, "");
    }
    onChange(questionId, inputValue);
  };

  const inputId = `textbox-${questionId}`;
  const labelId = `textbox-label-${questionId}`;
  const errorId = `${inputId}-error`;

  return (
    <div className={classes.container}>
      <Typography id={labelId} component="label" htmlFor={inputId} className={classes.questionText}>
        <RenderHTML content={questionText ?? ""} />
        {isMandatory && <span className={classes.requiredAsterisk}>*</span>}
      </Typography>
      <TextField
        id={inputId}
        value={value}
        onChange={handleChange}
        placeholder={questionHint || ""}
        variant="outlined"
        size="small"
        type="text"
        error={hasError && isMandatory}
        className={hasError && isMandatory ? classes.textFieldError : classes.textField}
        inputProps={{
          id: inputId,
          "aria-labelledby": labelId,
          "aria-required": isMandatory,
          "aria-describedby": hasError && isMandatory ? errorId : undefined,
          maxLength: 500,
          ...(isNumeric && { inputMode: "numeric", pattern: "[0-9]*" }),
        }}
      />
      {(isMandatory && (!value || value.trim() === "")) || (hasError && isMandatory) ? (
        <ErrorMessage id={errorId}>{`${questionText} ${ErrorMessages.InputRequired}`}</ErrorMessage>
      ) : null}
    </div>
  );
};
